import React, { useState, useRef, useEffect } from 'react';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { DownloadIcon } from 'src/components/ui-icons/upload';
import EyeIcon from 'src/components/ui-icons/reports/EyeIcon';
import Loading from 'src/components/Loading';
import { FlexCol } from 'src/utils/flexLayouts';
import { useFilePreview, isPreviewable } from 'src/hooks/useFilePreview';
import { BACKEND_URL } from '../../api/api';
import DeleteIcon from '../../ui-icons/deleteIcon';
import {
  uploadFileRequest,
  deleteFileRequest,
  downloadFileRequest,
  fetchFilesRequest,
} from '../../store/fileUploads/actions';
import { selectFilesByPrescID, selectFilesByLicenseID } from '../../store/fileUploads/selector';

type FileUploaderProps = {
  prescriptionId?: number;
  itemId?: number;
  listName?: string;
  idFieldName?: string;
  onFileCountChange?: (count: number) => void;
};

const FileUploader: React.FC<FileUploaderProps> = ({
  prescriptionId,
  itemId,
  listName,
  idFieldName,
  onFileCountChange
}) => {
  const dispatch = useAppDispatch();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { openPreview, previewLoading, previewFileId, PreviewModal } = useFilePreview(BACKEND_URL);

  const entityType: 'prescription' | 'license' | null =
    prescriptionId ? 'prescription' :
    (listName === 'LicensesUpload' && itemId) ? 'license' :
    null;

  const entityId = prescriptionId || itemId;

  const files = useAppSelector((state) => {
    if (prescriptionId) {
      return selectFilesByPrescID(prescriptionId)(state);
    } else if (itemId && entityType === 'license') {
      return selectFilesByLicenseID(itemId)(state);
    }
    return [];
  });

  useEffect(() => {
    if (onFileCountChange) {
      onFileCountChange(files.length);
    }
  }, [files.length, onFileCountChange]);

  useEffect(() => {
    if (entityId && entityType) {
      dispatch(fetchFilesRequest(
        entityType === 'prescription' ? prescriptionId : undefined,
        entityType === 'license' ? itemId : undefined,
        entityType
      ));
    }
  }, [entityId, entityType, prescriptionId, itemId, dispatch]);

  const handleUpload = (file: File) => {
    if (!entityId || !entityType) {
      return;
    }

    const tempId = `${file.name}_${Date.now()}`;
    dispatch(
      uploadFileRequest({
        file,
        prescID: entityType === 'prescription' ? prescriptionId : undefined,
        licensesID: entityType === 'license' ? itemId : undefined,
        tempId,
        entityType,
      })
    );
  };

  const handleFiles = (inputFiles: FileList) => {
    Array.from(inputFiles).forEach(handleUpload);
  };

  const handleDelete = (documentId: string, uploadId: number) => {
    dispatch(deleteFileRequest(
      documentId,
      uploadId,
      entityType || undefined,
      prescriptionId,
      itemId
    ));
  };

  const handleDownload = (fileUrl: string, fileName: string) => {
    dispatch(
      downloadFileRequest({
        id: 0,
        documentId: '',
        name: fileName,
        url: fileUrl,
        size: 0,
        mime: '',
        prescID: prescriptionId,
        licensesID: itemId,
      })
    );
  };

  const formatFileSize = (sizeInKB: number): string => {
    return (sizeInKB / 1024).toFixed(2);
  };

  return (
    <div className='w-full'>
      <div
        className={`w-full h-60 bg-[#F3F4F6] border border-dashed rounded-lg flex flex-col items-center justify-center
        ${isDragging ? 'border-blue-600 bg-blue-50' : 'border-slate-200'}`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        <input type='file' multiple className='hidden' ref={fileInputRef} onChange={(e) => e.target.files && handleFiles(e.target.files)} />

        <DocumentIcon className='w-6 h-6 text-[#637381] mb-2' />
        <div className='text-lg text-[#637381]'>
          Перетащите документ сюда или{' '}
          <button onClick={() => fileInputRef.current?.click()} className='text-blue-900 hover:text-blue-700'>
            выберите файл
          </button>
        </div>
        <p className='text-sm text-[#637381] mt-1'>Размер файла не более 50MB</p>
      </div>

      <div className='mt-4 space-y-4'>
        {files.map((fileInfo) => (
          <div key={fileInfo.documentId || fileInfo.tempId} className='flex flex-col p-3 bg-[#F3F4F6] rounded-lg'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center flex-1 min-w-0 mr-4'>
                <DocumentIcon className='w-5 h-5 text-slate-400 mr-3 flex-shrink-0' />
                <div className='truncate'>
                  <span className='text-base text-[#1F2A37]'>{fileInfo.name}</span>
                  <span className='text-base text-[#1F2A37] ml-2'>({formatFileSize(fileInfo.size)} MB)</span>
                </div>
              </div>

              <div className='flex items-center gap-4 flex-shrink-0'>
                {!fileInfo.isUploading && isPreviewable(fileInfo.url) && (
                  <button
                    type='button'
                    onClick={() => openPreview(fileInfo.url, fileInfo.uploadId)}
                    className='p-1'
                    title='Предпросмотр'
                  >
                    {previewLoading && previewFileId === fileInfo.uploadId ? (
                      <FlexCol className='flex w-6 h-6'>
                        <Loading />
                      </FlexCol>
                    ) : (
                      <EyeIcon />
                    )}
                  </button>
                )}
                {!fileInfo.isUploading && (
                  <button onClick={() => handleDownload(fileInfo.url, fileInfo.name)} className='p-1'>
                    <DownloadIcon />
                  </button>
                )}
                <button onClick={() => handleDelete(fileInfo.documentId, fileInfo.uploadId || 0)} className='p-1' disabled={fileInfo.isUploading}>
                  <DeleteIcon />
                </button>
              </div>
            </div>

            {fileInfo.isUploading && (
              <div className='w-full mt-2 h-1 bg-slate-200 rounded-full'>
                <div className='h-1 bg-blue-600 rounded-full transition-all' style={{ width: `${fileInfo.progress || 0}%` }} />
              </div>
            )}
          </div>
        ))}
      </div>
      {PreviewModal}
    </div>
  );
};

export default FileUploader;
